import { cn } from '@/lib/utils';
import hljs from 'highlight.js';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import CopyButton from './CopyButton';

import 'highlight.js/styles/monokai-sublime.css';

const Plot = lazy(() => import('react-plotly.js'));

interface CodeSnippetProps {
  language: string;
  children: string;
}

const HighlightedCode = ({ language, children }: CodeSnippetProps) => {
  const codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (codeRef.current) {
      const highlighted =
        codeRef.current.getAttribute('data-highlighted') === 'yes';
      if (!highlighted) {
        hljs.highlightElement(codeRef.current);
      }
    }
  }, []);

  return (
    <pre className="m-0">
      <code
        ref={codeRef}
        className={`language-${language} font-mono text-sm rounded-b-md block`}
      >
        {children}
      </code>
    </pre>
  );
};

interface CodeProps {
  children: React.ReactNode;
  node?: {
    children?: Array<{
      properties?: {
        className?: string[];
      };
      children?: Array<{
        value?: string;
      }>;
    }>;
  };
}

/**
 * Checks if JSON data is a valid Plotly chart specification.
 * A valid Plotly chart must have 'data' and 'layout' properties.
 */
function isValidPlotlyData(obj: any): boolean {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    Array.isArray(obj.data) &&
    typeof obj.layout === 'object'
  );
}

export default function CodeSnippet({ ...props }: CodeProps) {
  const [plotlyData, setPlotlyData] = useState<any>(null);
  const codeChildren = props.node?.children?.[0];
  const className = codeChildren?.properties?.className?.[0];
  const match = /language-(\w+)/.exec(className || '');
  const code = codeChildren?.children?.[0]?.value;
  const language = match?.[1];

  // Try to parse JSON and check if it's valid Plotly data
  useEffect(() => {
    if (language === 'json' && code) {
      try {
        console.log('[CodeSnippet] Parsing JSON code block:', {
          language,
          codeLength: code.length,
          codePreview: code.substring(0, 100) + (code.length > 100 ? '...' : '')
        });

        const parsed = JSON.parse(code);
        console.log('[CodeSnippet] JSON parsed successfully:', {
          hasData: 'data' in parsed,
          hasLayout: 'layout' in parsed,
          dataType: typeof parsed.data,
          layoutType: typeof parsed.layout
        });

        if (isValidPlotlyData(parsed)) {
          console.log('[CodeSnippet] Valid Plotly data detected, rendering chart');
          setPlotlyData(parsed);
        } else {
          console.log('[CodeSnippet] JSON parsed but not valid Plotly data', { keys: Object.keys(parsed) });
          setPlotlyData(null);
        }
      } catch (error) {
        console.debug('[CodeSnippet] JSON parse error or not Plotly data:', {
          error: error instanceof Error ? error.message : String(error),
          language,
          codeLength: code.length
        });
        // Not valid JSON or not Plotly data, render as code
        setPlotlyData(null);
      }
    } else if (language !== 'json') {
      console.debug('[CodeSnippet] Not a JSON code block, language:', language);
    }
  }, [code, language]);

  const showSyntaxHighlighter = match && code && !plotlyData;

  const highlightedCode = showSyntaxHighlighter ? (
    <HighlightedCode language={match[1]}>{code}</HighlightedCode>
  ) : null;

  const nonHighlightedCode = showSyntaxHighlighter ? null : (
    <div
      className={cn('rounded-b-md overflow-x-auto bg-accent', code && 'p-2')}
    >
      <code className="whitespace-pre-wrap">{code}</code>
    </div>
  );

  // Ensure Plotly layouts are responsive and don't force fixed width/height.
  const sanitizePlotlyLayout = (layout?: any) => {
    const { width: _w, height: _h, margin, ...rest } = layout || {};
    const defaultMargins = { l: 40, r: 28, t: 50, b: 40, pad: 0 };
    return { autosize: true, ...rest, margin: { ...defaultMargins, ...(margin || {}) } };
  };

  // Render Plotly chart if detected
  if (plotlyData) {
    return (
      <Card className="relative my-2">
        <CardHeader className="flex flex-row items-center justify-between py-1 px-4">
          <span className="text-sm text-muted-foreground">
            Plotly Chart
          </span>
          <CopyButton content={code} />
        </CardHeader>
        <CardContent className="p-0">
          <div className="relative w-full h-[500px] min-w-0 overflow-hidden" style={{ width: '100%', height: '500px', position: 'relative' }}>
            <Suspense fallback={<Skeleton className="h-full rounded-b-md" />}>
              <Plot
                data={plotlyData.data}
                layout={sanitizePlotlyLayout(plotlyData.layout)}
                frames={plotlyData.frames}
                config={{ responsive: true, ...plotlyData.config }}
                style={{
                  width: '100%',
                  height: '100%'
                }}
                useResizeHandler={true}
              />
            </Suspense>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative my-2">
      <CardHeader className="flex flex-row items-center justify-between py-1 px-4">
        <span className="text-sm text-muted-foreground">
          {match?.[1] || 'Raw code'}
        </span>
        <CopyButton content={code} />
      </CardHeader>
      <CardContent className="p-0">
        {highlightedCode}
        {nonHighlightedCode}
      </CardContent>
    </Card>
  );
}
